import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginatorIntl } from '@angular/material/paginator';
import {
  DataTableColumnDef,
  DataTableEditEvent,
  DataTablePageChange,
  DataTableSelectionChange,
  DataTableSortChange,
  DataTableSortDirection,
} from './data-table.models';

const ROW_ACTIONS_COLUMN = '_rowActions';
const ROW_SELECT_COLUMN = '_rowSelect';

function getValue(obj: unknown, path: string): unknown {
  if (obj == null || !path) return undefined;
  return path.split('.').reduce((o: unknown, k: string) => (o as Record<string, unknown>)?.[k], obj);
}

@Component({
    selector: 'app-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DataTableComponent<T extends Record<string, unknown>> implements OnChanges {
  @Input() items: T[] = [];
  @Input() total = 0;
  @Input() loading = false;
  @Input() columns: DataTableColumnDef<T>[] = [];
  @Input() editable = false;
  @Input() pageSize = 25;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input() sortBy = '';
  @Input() sortDirection: DataTableSortDirection = 'asc';
  @Input() skip = 0;
  @Input() filterValues: Record<string, unknown> = {};
  @Input() rowIdKey = 'id';
  @Input() selectable = false;
  @Input() excludedIds: Set<unknown> = new Set();
  /** When set, shows a right-aligned toolbar button that toggles the projected [data-table-actions] panel. */
  @Input() actionLabel: string | null = null;
  @Input() actionIcon: string | null = null;

  @Output() pageChange = new EventEmitter<DataTablePageChange>();
  @Output() sortChange = new EventEmitter<DataTableSortChange>();
  @Output() filterApply = new EventEmitter<Record<string, unknown>>();
  @Output() edit = new EventEmitter<DataTableEditEvent<T>>();
  @Output() selectionChange = new EventEmitter<DataTableSelectionChange>();

  filterPanelOpen = false;
  actionPanelOpen = false;
  filterForm: UntypedFormGroup = this.fb.group({});
  filterableColumns: DataTableColumnDef<T>[] = [];
  activeFilterCount = 0;
  selectSearchTerms: Record<string, string> = {};

  editingRowId: unknown = null;
  draft: Partial<T> = {};

  displayedColumns: string[] = [];
  readonly rowActionsColumn = ROW_ACTIONS_COLUMN;
  readonly rowSelectColumn = ROW_SELECT_COLUMN;

  constructor(private fb: UntypedFormBuilder, private paginatorIntl: MatPaginatorIntl, private cdr: ChangeDetectorRef) {
    this.paginatorIntl.itemsPerPageLabel = 'Itens por página:';
    this.paginatorIntl.nextPageLabel = 'Próxima página';
    this.paginatorIntl.previousPageLabel = 'Página anterior';
    this.paginatorIntl.firstPageLabel = 'Primeira página';
    this.paginatorIntl.lastPageLabel = 'Última página';
    this.paginatorIntl.getRangeLabel = (page, pageSize, length) => {
      if (length === 0) return '0 de 0';
      const start = page * pageSize + 1;
      const end = Math.min(start + pageSize - 1, length);
      return `${start} – ${end} de ${length}`;
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columns']) {
      this.filterableColumns = (this.columns || []).filter((c) => c.filterable);
      this.buildFilterForm();
    }
    if (changes['columns'] || changes['editable'] || changes['selectable']) {
      this.updateDisplayedColumns();
    }
    if (changes['filterValues'] && this.filterForm) {
      this.patchFilterForm(this.filterValues);
    }
    this.cdr.detectChanges();
  }

  private buildFilterForm(): void {
    const existing = this.filterForm?.getRawValue() || {};
    const group: Record<string, unknown> = {};
    this.filterableColumns.forEach((c) => {
      const defaultVal = c.filterMultiple ? [] : (c.type === 'boolean' ? '' : '');
      group[c.key] = [existing[c.key] ?? this.filterValues[c.key] ?? defaultVal];
    });
    this.filterForm = this.fb.group(group);
  }

  private patchFilterForm(values: Record<string, unknown>): void {
    if (!this.filterForm) return;
    const patch: Record<string, unknown> = {};
    this.filterableColumns.forEach(c => {
      const defaultVal = c.filterMultiple ? [] : '';
      patch[c.key] = values?.[c.key] ?? defaultVal;
    });
    this.filterForm.patchValue(patch, { emitEvent: false });
    this.activeFilterCount = Object.values(patch).filter(v => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== '' && v != null;
    }).length;
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = (this.columns || []).map((c) => c.key);
    if (this.selectable) {
      this.displayedColumns = [ROW_SELECT_COLUMN, ...this.displayedColumns];
    }
    if (this.editable) {
      this.displayedColumns = [...this.displayedColumns, ROW_ACTIONS_COLUMN];
    }
  }

  getValue(row: T, col: DataTableColumnDef<T>): unknown {
    return getValue(row, col.key);
  }

  getDisplayValue(row: T, col: DataTableColumnDef<T>): string {
    const v = getValue(row, col.key);
    if (v == null) return '';
    if (col.displayFormat === 'percent' && typeof v === 'number') {
      return Math.round(v * 10000) / 100 + '%';
    }
    return String(v);
  }

  getSortKey(col: DataTableColumnDef<T>): string {
    return col.sortKey ?? col.key;
  }

  isSortActive(col: DataTableColumnDef<T>): boolean {
    return this.sortBy === this.getSortKey(col);
  }

  onSort(col: DataTableColumnDef<T>): void {
    const sortKey = this.getSortKey(col);
    const next: DataTableSortDirection =
      this.sortBy === sortKey && this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sortChange.emit({ sortBy: sortKey, sortDirection: next });
  }

  get pageIndex(): number {
    return this.pageSize > 0 ? Math.floor(this.skip / this.pageSize) : 0;
  }

  onPage(e: { pageIndex: number; pageSize: number }): void {
    this.pageChange.emit({
      skip: e.pageIndex * e.pageSize,
      take: e.pageSize,
    });
  }

  onFilterApply(): void {
    const raw = this.filterForm.getRawValue() as Record<string, unknown>;
    this.activeFilterCount = Object.values(raw).filter(v => {
      if (Array.isArray(v)) return v.length > 0;
      return v !== '' && v != null;
    }).length;
    this.filterApply.emit(raw);
  }

  onFilterClear(): void {
    this.selectSearchTerms = {};
    this.filterForm.reset(
      this.filterableColumns.reduce((acc, c) => {
        acc[c.key] = c.filterMultiple ? [] : '';
        return acc;
      }, {} as Record<string, unknown>)
    );
    this.activeFilterCount = 0;
    this.filterApply.emit(this.filterForm.getRawValue() as Record<string, unknown>);
  }

  toggleActionPanel(): void {
    this.actionPanelOpen = !this.actionPanelOpen;
    if (this.actionPanelOpen) this.filterPanelOpen = false;
  }

  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
    if (this.filterPanelOpen) this.actionPanelOpen = false;
  }

  isEditing(row: T): boolean {
    const id = (row as Record<string, unknown>)[this.rowIdKey] ?? (row as Record<string, unknown>)['Id'];
    return id !== undefined && id === this.editingRowId;
  }

  startEdit(row: T): void {
    const id = (row as Record<string, unknown>)[this.rowIdKey] ?? (row as Record<string, unknown>)['Id'];
    this.editingRowId = id;
    this.draft = {};
    this.columns.filter((c) => c.editable).forEach((c) => {
      let v = this.getValue(row, c);
      if (c.displayFormat === 'percent' && typeof v === 'number') {
        v = Math.round(v * 10000) / 100;
      }
      (this.draft as Record<string, unknown>)[c.key] = v;
    });
  }

  cancelEdit(): void {
    this.editingRowId = null;
    this.draft = {};
  }

  saveEdit(row: T): void {
    if (Object.keys(this.draft).length === 0) {
      this.cancelEdit();
      return;
    }
    const changes = { ...this.draft };
    this.columns.filter(c => c.editable && c.displayFormat === 'percent').forEach(c => {
      const v = (changes as Record<string, unknown>)[c.key];
      if (typeof v === 'number') {
        (changes as Record<string, unknown>)[c.key] = Math.round(v * 100) / 10000;
      }
    });
    this.edit.emit({ row, changes });
    this.cancelEdit();
  }

  getDraft(col: DataTableColumnDef<T>): unknown {
    return (this.draft as Record<string, unknown>)[col.key];
  }

  setDraft(col: DataTableColumnDef<T>, value: unknown): void {
    (this.draft as Record<string, unknown>)[col.key] = value;
  }

  isDraftInvalid(col: DataTableColumnDef<T>): boolean {
    if (col.type !== 'number' || col.min == null && col.max == null) return false;
    const v = (this.draft as Record<string, unknown>)[col.key];
    if (typeof v !== 'number' || isNaN(v)) return true;
    const scale = col.displayFormat === 'percent' ? 100 : 1;
    if (col.min != null && v < col.min * scale) return true;
    if (col.max != null && v > col.max * scale) return true;
    return false;
  }

  getEditMin(col: DataTableColumnDef<T>): number | null {
    if (col.min == null) return null;
    return col.displayFormat === 'percent' ? col.min * 100 : col.min;
  }

  getEditMax(col: DataTableColumnDef<T>): number | null {
    if (col.max == null) return null;
    return col.displayFormat === 'percent' ? col.max * 100 : col.max;
  }

  hasDraftErrors(): boolean {
    return this.columns.some((c) => c.editable && this.isDraftInvalid(c));
  }

  isEditable(col: DataTableColumnDef<T>): boolean {
    return !!col.editable;
  }

  onSelectSearch(key: string, term: string): void {
    this.selectSearchTerms[key] = term;
  }

  getFilteredOptions(col: DataTableColumnDef<T>): string[] {
    const options = col.filterOptions || [];
    const term = (this.selectSearchTerms[col.key] || '').toLowerCase();
    if (!term) return options;
    const selected: unknown = this.filterForm?.get(col.key)?.value;
    const selectedSet = new Set(Array.isArray(selected) ? selected as string[] : []);
    return options.filter(o => selectedSet.has(o) || o.toLowerCase().includes(term));
  }

  onSelectPanelToggle(key: string, opened: boolean): void {
    if (opened) {
      setTimeout(() => {
        const panel = document.querySelector('.searchable-select-panel') as HTMLElement;
        if (panel) panel.scrollTop = 0;
      });
    } else {
      this.selectSearchTerms[key] = '';
    }
  }

  // Selection
  private getRowId(row: T): unknown {
    return (row as Record<string, unknown>)[this.rowIdKey] ?? (row as Record<string, unknown>)['Id'];
  }

  isRowSelected(row: T): boolean {
    return !this.excludedIds.has(this.getRowId(row));
  }

  get allPageSelected(): boolean {
    return this.items.length > 0 && this.items.every(r => this.isRowSelected(r));
  }

  get somePageSelected(): boolean {
    if (this.items.length === 0) return false;
    const hasSelected = this.items.some(r => this.isRowSelected(r));
    return hasSelected && !this.allPageSelected;
  }

  toggleRow(row: T): void {
    const id = this.getRowId(row);
    const next = new Set(this.excludedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.selectionChange.emit({ excludedIds: next });
  }

  toggleAllPage(): void {
    const next = new Set(this.excludedIds);
    if (this.allPageSelected) {
      this.items.forEach(r => next.add(this.getRowId(r)));
    } else {
      this.items.forEach(r => next.delete(this.getRowId(r)));
    }
    this.selectionChange.emit({ excludedIds: next });
  }
}
